import type { EventsListResponse } from '@loci/api';

import { API_ENDPOINTS } from '@/config/api';

/* ------------------------------------------------------------------ */
/*  Response types — derived from the OpenAPI contract                 */
/*                                                                     */
/*  `@loci/api` re-exports `EventsListResponse` which is generated */
/*  from the `GET /events/search` operation in                         */
/*  `specs/openapi/backend-api-v1.yaml`. Re-using it here means the    */
/*  frontend envelope shape tracks the backend automatically.          */
/* ------------------------------------------------------------------ */

export type SearchResponse = EventsListResponse;
export type SearchPagination = EventsListResponse['pagination'];
export type SearchEvent = EventsListResponse['events'][number];

export interface SearchSuggestionItem {
  type: 'artist' | 'venue' | 'event' | 'location';
  value: string;
  label: string;
  category?: string;
  /**
   * Only present when `type === 'location'` (WEB-965). Distinguishes a city
   * suggestion (e.g. "Charlotte, NC" — the only kind the backend's
   * `location` sub-query sources today) from a neighborhood suggestion, so
   * consumers don't route every `location` row the same way. See
   * `classifyLocationSuggestionKind` in `@/lib/neighborhoods/clt-slugs`.
   */
  locationKind?: 'city' | 'neighborhood';
}

export interface SearchSuggestionsResponse {
  suggestions: SearchSuggestionItem[];
}

/**
 * Discriminated result of {@link fetchSuggestions}. The hook needs to
 * distinguish between transport-level failures (preserve last list, no
 * countdown), 429 throttles (preserve last list, show countdown), aborts
 * (no-op), and success (replace list) — collapsing them all to "empty array"
 * (the original `fetchSuggestions` behaviour) is what made the suggestion
 * list flicker to empty on every transient network blip (FRE-821 AC 2).
 *
 * Default Retry-After (2 s) is intentionally short for the suggestion
 * endpoint: backend caps at 10 req/s per IP and the user is mid-keystroke,
 * so a long lock-out would feel broken. `parseSuggestionsRetryAfter` clamps
 * the parsed value to a sane upper bound to defend against hostile headers.
 */
export type FetchSuggestionsResult =
  | { kind: 'ok'; suggestions: SearchSuggestionItem[] }
  | { kind: 'rate-limited'; retryAfterSeconds: number }
  | { kind: 'error' }
  | { kind: 'aborted' };

/** HTTP status the backend rate limiter returns. */
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Default Retry-After window (seconds) used when a 429 arrives with no
 * usable `Retry-After` header. FRE-821 fixes this at 2 s — short enough
 * the user can resume typing quickly, long enough to actually clear the
 * limiter's 1-second window plus a safety buffer.
 */
export const DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS = 2;

/** Upper bound (seconds) so a hostile / bogus header can't freeze the UI. */
const MAX_SUGGESTIONS_RETRY_AFTER_SECONDS = 60;

/* ------------------------------------------------------------------ */
/*  Request params                                                     */
/* ------------------------------------------------------------------ */

export interface SearchParams {
  q?: string;
  category?: string;
  sort?: string;
  lat?: number;
  lon?: number;
  radius?: number;
  dateFrom?: string;
  dateTo?: string;
  priceMin?: number;
  priceMax?: number;
  venueType?: string;
  wheelchair?: boolean;
  parking?: boolean;
  transit?: boolean;
  includeFree?: boolean;
  page?: number;
  pageSize?: number;
}

/* ------------------------------------------------------------------ */
/*  API functions                                                      */
/* ------------------------------------------------------------------ */

/**
 * Search events via the unified search endpoint.
 *
 * Accepts an optional `AbortSignal` for in-flight cancellation.
 */
export async function searchEvents(
  params: SearchParams,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const url = new URL(API_ENDPOINTS.events.search);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await fetch(url.toString(), { signal });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  return response.json() as Promise<SearchResponse>;
}

/**
 * Parse an RFC 9110 §10.2.3 `Retry-After` value into whole seconds, clamped
 * to {@link MAX_SUGGESTIONS_RETRY_AFTER_SECONDS}. Falls back to
 * {@link DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS} on missing / malformed /
 * negative / absurd input so the UI countdown is always a sane positive
 * integer.
 *
 * Accepts both wire forms:
 *  - delta-seconds: bare non-negative integer (`"3"`)
 *  - HTTP-date: absolute timestamp (`"Wed, 21 Oct 2026 07:28:00 GMT"`)
 *
 * `now` is injectable so the test suite can pin time deterministically.
 */
export function parseSuggestionsRetryAfter(
  raw: string | null | undefined,
  now: number = Date.now(),
): number {
  const clamp = (seconds: number): number => {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS;
    }
    return Math.min(Math.ceil(seconds), MAX_SUGGESTIONS_RETRY_AFTER_SECONDS);
  };

  if (raw === null || raw === undefined) return DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS;

  const trimmed = raw.trim();
  if (trimmed.length === 0) return DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS;

  if (/^\d+$/.test(trimmed)) {
    return clamp(Number.parseInt(trimmed, 10));
  }

  const target = Date.parse(trimmed);
  if (Number.isNaN(target)) return DEFAULT_SUGGESTIONS_RETRY_AFTER_SECONDS;
  return clamp((target - now) / 1000);
}

/**
 * Fetch autocomplete suggestions for the search bar.
 *
 * Short queries (< 2 characters) return an empty list immediately. `city` is
 * the lowercase slug the suggestions are scoped to (API-1821).
 * Returns a discriminated result so the caller can render a quiet
 * "slow down" hint on 429 and preserve the previous list on transient
 * errors instead of flickering to empty (FRE-821).
 */
export async function fetchSuggestions(
  query: string,
  city: string,
  signal?: AbortSignal,
): Promise<FetchSuggestionsResult> {
  if (query.length < 2) {
    return { kind: 'ok', suggestions: [] };
  }

  const url = new URL(API_ENDPOINTS.events.searchSuggestions);
  url.searchParams.set('q', query);
  // API-1821 — suggestions are scoped to one city; `city` is required.
  url.searchParams.set('city', city.toLowerCase());

  try {
    const response = await fetch(url.toString(), { signal });

    if (response.status === HTTP_TOO_MANY_REQUESTS) {
      const retryAfterRaw = response.headers.get('Retry-After');
      return {
        kind: 'rate-limited',
        retryAfterSeconds: parseSuggestionsRetryAfter(retryAfterRaw),
      };
    }

    if (!response.ok) {
      return { kind: 'error' };
    }

    const body = (await response.json()) as SearchSuggestionsResponse;
    // Defensive: backend should always return `{ suggestions: [] }`, but if
    // a future bug hands back a bare array or missing field, surface it as
    // an empty list rather than throwing in the consumer.
    return { kind: 'ok', suggestions: Array.isArray(body?.suggestions) ? body.suggestions : [] };
  } catch (err) {
    // `fetch` rejects with an `AbortError` (DOMException name === 'AbortError')
    // when the caller aborts via `signal`. Distinguish so the hook can no-op
    // instead of marking the request as failed — an aborted in-flight should
    // not be treated as a network error.
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { kind: 'aborted' };
    }
    if (signal?.aborted) {
      return { kind: 'aborted' };
    }
    return { kind: 'error' };
  }
}
